import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useTheme } from '@/contexts/ThemeContext';
import AppLayout from '@/layouts/AppLayout';
import { CalendarEvent } from '@/types';
import { router } from '@inertiajs/react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import React, { useState } from 'react';
import CalendarGrid from './Partials/CalendarGrid';
import SubscribeModal from './Partials/SubscribeModal';

interface Props {
    events: CalendarEvent[];
    year: number;
    month: number;
    current_date: string;
    subscription_token: string;
}

const CalendarsContent: React.FC<Props> = ({
    events,
    year,
    month,
    current_date,
    subscription_token,
}) => {
    const { theme } = useTheme();
    const [showSubscribeModal, setShowSubscribeModal] = useState(false);

    const monthNames = [
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre',
    ];

    const handlePreviousMonth = () => {
        let newMonth = month - 1;
        let newYear = year;

        if (newMonth < 1) {
            newMonth = 12;
            newYear = year - 1;
        }

        router.get(`/calendars/?year=${newYear}&month=${newMonth}`);
    };

    const handleNextMonth = () => {
        let newMonth = month + 1;
        let newYear = year;

        if (newMonth > 12) {
            newMonth = 1;
            newYear = year + 1;
        }

        router.get(`/calendars/?year=${newYear}&month=${newMonth}`);
    };

    const handleToday = () => {
        // Parse current_date string directly to avoid timezone issues
        const [todayYear, todayMonth] = current_date.split('-').map(Number);
        router.get(`/calendars/?year=${todayYear}&month=${todayMonth}`);
    };

    if (theme === 'cfinance') {
        return (
            <AppLayout>
                <PageTitle title="Calendario" />
                <div className="mx-auto w-full space-y-4">
                    <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                        Calendario
                    </h1>
                    <div className="relative w-full overflow-hidden rounded-xl" style={{ paddingBottom: '55%' }}>
                        <iframe
                            src="https://calendar.google.com/calendar/embed?src=ifbgjscbk6efafc8bi89he7fcg%40group.calendar.google.com&ctz=Europe%2FMadrid"
                            className="absolute inset-0 h-full w-full rounded-xl border-0"
                            frameBorder={0}
                            scrolling="no"
                            title="Calendario ConquerFinance"
                        />
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <PageTitle title="Calendario" />
            <div className="mx-auto w-full space-y-4 px-4 sm:max-w-4xl sm:space-y-6 sm:px-0">
                {/* Header */}
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                        Calendario
                    </h1>
                </div>

                {/* Calendar Navigation */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 sm:gap-2">
                        <button
                            onClick={handlePreviousMonth}
                            className="rounded-md p-1 text-gray-600 transition-colors hover:bg-gray-100 sm:p-1.5"
                        >
                            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>

                        <h2 className="min-w-[120px] text-center text-sm font-semibold text-gray-900 sm:min-w-[140px] sm:text-base">
                            {monthNames[month - 1]} {year}
                        </h2>

                        <button
                            onClick={handleNextMonth}
                            className="rounded-md p-1 text-gray-600 transition-colors hover:bg-gray-100 sm:p-1.5"
                        >
                            <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                    </div>

                    <button
                        onClick={handleToday}
                        className="rounded-md px-2 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-100 sm:px-3 sm:py-1.5 sm:text-xs"
                    >
                        Hoy
                    </button>
                </div>

                {/* Calendar Grid */}
                <CalendarGrid
                    events={events}
                    year={year}
                    month={month}
                    currentDate={current_date}
                />

                {/* Subscribe Section */}
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                        Suscríbete al calendario
                    </h3>
                    <p className="text-xs text-gray-600">
                        Recibe todos los eventos automáticamente en tu
                        aplicación de calendario
                    </p>
                    <PrimaryButton
                        type="button"
                        onClick={() => setShowSubscribeModal(true)}
                        size="full-width"
                    >
                        <Download className="h-4 w-4" />
                        Suscribirse
                    </PrimaryButton>
                </div>

                {/* Subscribe Modal */}
                {showSubscribeModal ? (
                    <SubscribeModal
                        onClose={() => setShowSubscribeModal(false)}
                        subscriptionToken={subscription_token}
                    />
                ) : null}
            </div>
        </AppLayout>
    );
};

const Calendars: React.FC<Props> = (props) => {
    return (
        <ThemeWrapper>
            <CalendarsContent {...props} />
        </ThemeWrapper>
    );
};

export default Calendars;
